/* ==========================================================================
   Greasy Miniatures — product form
   Custom element <gm-product-form> handling:
     - variant resolution from option inputs
     - price, availability, URL and gallery updates on variant change
     - AJAX add to cart against /cart/add.js

   Option NAMES in this store are inconsistent ("Team Options", "Team Option",
   "Team options", "Team Optioins"), so nothing here reads an option name.
   Options are matched by position only.

   The form also works with JavaScript disabled: it is a real POST to
   /cart/add, and this script only enhances it.
   ========================================================================== */

(function () {
  'use strict';

  class GreasyProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.submitButton = this.querySelector('[data-add-to-cart]');
      this.submitText = this.querySelector('[data-add-to-cart-text]');
      /* The price block sits above the form in the layout, so it is a sibling
         of this component rather than a descendant. Fall back to page scope. */
      this.priceTarget =
        this.querySelector('[data-price-target]') ||
        document.querySelector('[data-price-target]');
      this.availability = this.querySelector('[data-availability]');
      this.errorTarget = this.querySelector('[data-form-error]');
      this.optionInputs = Array.from(this.querySelectorAll('[data-option-position]'));

      this.variants = this.readVariants();
      if (!this.form || !this.variants.length) return;

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', this.onOptionChange);
      });
      this.form.addEventListener('submit', this.onSubmit);
    }

    readVariants() {
      const script = this.querySelector('[data-variant-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /* ----------------------------------------------------------------
       Variant resolution — by option position, never by option name.
       ---------------------------------------------------------------- */

    selectedOptions() {
      const values = [];
      this.optionInputs.forEach((input) => {
        if (input.type === 'radio' && !input.checked) return;
        values[Number(input.dataset.optionPosition) - 1] = input.value;
      });
      return values;
    }

    matchVariant(values) {
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === values[index])
      );
    }

    onOptionChange() {
      const variant = this.matchVariant(this.selectedOptions());
      this.currentVariant = variant;

      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateUrl(variant);
      this.updateGallery(variant);
      this.clearError();
    }

    updateVariantInput(variant) {
      if (!this.variantInput) return;
      this.variantInput.value = variant ? variant.id : '';
    }

    updatePrice(variant) {
      if (!this.priceTarget || !variant) return;

      const value = this.priceTarget.querySelector('[data-price-value]');
      if (value) value.textContent = variant.price_formatted;

      const onSale = Boolean(
        variant.compare_at_price && variant.compare_at_price > variant.price
      );

      const priceRoot = this.priceTarget.querySelector('[data-price]') || this.priceTarget;
      priceRoot.classList.toggle('gm-price--sale', onSale);

      const compare = this.priceTarget.querySelector('[data-price-compare]');
      if (compare) {
        compare.hidden = !onSale;
        const compareValue = compare.querySelector('[data-price-compare-value]');
        if (compareValue && onSale) {
          compareValue.textContent = variant.compare_at_price_formatted || '';
        }
      }
    }

    updateAvailability(variant) {
      const available = Boolean(variant && variant.available);

      if (this.submitButton) {
        this.submitButton.disabled = !available;
      }
      if (this.submitText) {
        let key = 'add';
        if (!variant) key = 'unavailable';
        else if (!variant.available) key = 'soldOut';
        this.submitText.textContent = this.dataset[key + 'Label'] || this.submitText.textContent;
      }
      if (this.availability) {
        this.availability.textContent = available
          ? this.dataset.inStockLabel || ''
          : this.dataset.soldOutLabel || '';
      }

      /* Mark option values that lead to no purchasable variant. */
      this.optionInputs.forEach((input) => {
        if (input.type !== 'radio') return;
        const position = Number(input.dataset.optionPosition) - 1;
        const trial = this.selectedOptions();
        trial[position] = input.value;
        const match = this.matchVariant(trial);
        const label = input.nextElementSibling;
        if (label) {
          label.classList.toggle(
            'gm-option__value--unavailable',
            !(match && match.available)
          );
        }
      });
    }

    updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateGallery(variant) {
      if (!variant || !variant.featured_media_id) return;
      const target = document.querySelector(
        '[data-gallery-main][data-media-id="' + variant.featured_media_id + '"]'
      );
      const thumb = document.querySelector(
        '[data-gallery-thumb][data-media-id="' + variant.featured_media_id + '"]'
      );
      if (thumb) thumb.click();
      else if (target) target.scrollIntoView({ block: 'nearest' });
    }

    /* ----------------------------------------------------------------
       Add to cart
       ---------------------------------------------------------------- */

    async onSubmit(event) {
      event.preventDefault();
      this.clearError();

      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });

        const data = await response.json();

        if (!response.ok) {
          this.showError(data.description || data.message);
          return;
        }

        const cart = await fetch(window.Shopify.routes.root + 'cart.js', {
          headers: { Accept: 'application/json' }
        }).then((res) => res.json());

        if (window.GreasyTheme) window.GreasyTheme.emitCartUpdate(cart);
        this.flashAdded();
      } catch (error) {
        this.showError(this.dataset.genericErrorLabel);
      } finally {
        if (this.submitButton) {
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-busy');
        }
      }
    }

    flashAdded() {
      if (!this.submitText) return;
      const original = this.submitText.textContent;
      this.submitText.textContent = this.dataset.addedLabel || original;
      window.setTimeout(() => {
        this.submitText.textContent = original;
      }, 2000);
    }

    showError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message || this.dataset.genericErrorLabel || '';
      this.errorTarget.hidden = false;
    }

    clearError() {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = '';
      this.errorTarget.hidden = true;
    }
  }

  if (!customElements.get('gm-product-form')) {
    customElements.define('gm-product-form', GreasyProductForm);
  }

  /* ------------------------------------------------------------------
     Gallery thumbnails
     ------------------------------------------------------------------ */

  class GreasyGallery extends HTMLElement {
    connectedCallback() {
      this.mains = Array.from(this.querySelectorAll('[data-gallery-main]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId));
      });
    }

    show(mediaId) {
      this.mains.forEach((main) => {
        main.hidden = main.dataset.mediaId !== mediaId;
      });
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === mediaId));
      });
    }
  }

  if (!customElements.get('gm-gallery')) {
    customElements.define('gm-gallery', GreasyGallery);
  }
})();
